import { EXTERNAL_LINKS } from '@/constants/routes';
import { getRouteByKey } from '@/constants/routes.mapping';
import type { NavItemData } from '@/types/header.types';

function r(key: string, fallback?: string): string {
  return getRouteByKey(key)?.marketingUrl ?? fallback ?? '/';
}

const ROUTE = {
  conditions: r('conditions', '/conditions'),
  specialists: r('our-specialists'),
  specialities: r('specialities'),
  privateHealthPackages: r('health-packages'),
  pharmacy: r('pharmacy'),
  longevityCentre: r('longevity-centre'),
  newPatientInformation: r('new-patient-information'),
  pricingAndPayments: r('pricing-and-payments'),
  forClinicians: r('patient-referrals'),
  preparingForYourAppointment: r('prepare-for-your-appointment'),
  gettingHere: r('getting-here'),
  leaveFeedback: r('leave-feedback'),
  contactUs: r('contact-us'),
  aboutUs: r('about-lips'),
  investorRelations: r('investor-relations'),
  careers: r('clinicians-and-referrals'),
  blogs: r('health-blogs'),
  // technogymEvent: r('technogym-event'),
  postSurgery: r('post-surgery-recovery'),
  emergencyClinic: r('speciality-urgent-care'),
  gp: r('speciality-general-practice'),
  radiology: r('speciality-radiology'),
  ultrasound: r('speciality-ultrasound'),
  boneScan: r('speciality-bone-scan'),
  liverHealth: r('speciality-liver-health-fibroscan'),
  bloodTests: r('speciality-blood-tests'),
  dental: r('speciality-dental'),
  physiotherapy: r('speciality-physiotherapy'),
  aesthetics: r('speciality-aesthetics-clinic'),
  nurses: r('meet-our-nurses-and-care-team'),
};

export const megaMenuData: NavItemData[] = [
  {
    id: 'find-care',
    label: 'Find Care',
    href: ROUTE.specialities,
    hasDropdown: true,
    columns: [
      {
        title: 'Start Here',
        width: 'narrow',
        items: [
          { label: 'Search All Specialists', href: ROUTE.specialists },
          {
            label: 'Browse All Specialities',
            href: ROUTE.specialities,
          },
          { label: 'Health Packages', href: ROUTE.privateHealthPackages },
          {
            label: 'Urgent Care Centre',
            href: ROUTE.emergencyClinic,
          },
          {
            label: 'Longevity Centre',
            href: ROUTE.longevityCentre,
          },
        ],
      },
      {
        title: 'Tests & Scans',
        width: 'narrow',
        dynamicSource: 'tests-scans',
        dynamicLimit: 6,
      },
      {
        title: 'Popular Specialities',
        width: 'narrow',
        dynamicSource: 'specialities',
        dynamicLimit: 6,
        viewAll: {
          label: 'View All Specialities',
          href: ROUTE.specialities,
        },
      },
    ],
    featuredCard: {
      badge: 'NOT SURE WHERE TO START?',
      title: 'Book a private GP first',
      description:
        'Our GPs can assess your symptoms and refer you to the right consultant - same day in most cases.',
      ctaLabel: 'Book a GP Appointment',
      href: ROUTE.gp,
    },
  },
  {
    id: 'our-specialists',
    label: 'Our Specialists',
    href: ROUTE.specialists,
    hasDropdown: true,
    columns: [
      {
        title: 'Start Here',
        width: 'narrow',
        items: [
          { label: 'Search All Specialists', href: ROUTE.specialists },
          {
            label: 'Find a Surgeon',
            href: `${ROUTE.specialists}?type=surgeon`,
          },
          {
            label: 'Urgent Care Centre',
            href: ROUTE.emergencyClinic,
          },
          {
            label: 'Longevity Centre',
            href: ROUTE.longevityCentre,
          },
        ],
      },
      {
        title: 'Browse by Speciality',
        width: 'narrow',
        dynamicSource: 'specialities',
        randomize: true,
        dynamicLimit: 6,
        viewAll: {
          label: 'View All Specialities',
          href: `${ROUTE.specialities}?filter=all`,
        },
      },
      {
        title: 'Featured Specialists',
        type: 'consultants',
        width: 'wide',
        dynamicSource: 'specialists',
        dynamicLimit: 4,
        viewAll: {
          label: 'View All Specialists',
          href: ROUTE.specialists,
        },
      },
    ],
    featuredCard: {
      badge: 'WORLD-CLASS CARE',
      title: 'Over 100 leading consultants under one roof',
      description:
        'AT LIPS consultants are senior consultants with NHS and international credentials. Many are recognised as leaders in their field. Browse profiles, read patient reviews and book directly.',
      ctaLabel: 'Browse all specialists',
      href: ROUTE.specialists,
    },
  },
  {
    id: 'services',
    label: 'Specialities',
    href: ROUTE.specialities,
    hasDropdown: true,
    columns: [
      {
        title: 'Primary Care',
        width: 'narrow',
        items: [
          { label: 'GP (General Practice)', href: ROUTE.gp },
          { label: 'Imaging', href: ROUTE.radiology },
          { label: 'Ultrasound', href: ROUTE.ultrasound },
          { label: 'Bone Scan', href: ROUTE.boneScan },
          {
            label: 'Liver Health (FibroScan)',
            href: ROUTE.liverHealth,
          },
          {
            label: 'Urgent Care Centre',
            href: ROUTE.emergencyClinic,
          },
          {
            label: 'Blood Tests',
            href: ROUTE.bloodTests,
          },
        ],
      },
      {
        title: 'Everyday Services',
        width: 'narrow',
        items: [
          { label: 'Dental', href: ROUTE.dental },
          { label: 'Physiotherapy', href: ROUTE.physiotherapy },
          {
            label: 'Aesthetics Clinic',
            href: ROUTE.aesthetics,
          },
        ],
      },
      {
        title: 'Specialities & Surgical Care',
        width: 'wide',
        dynamicSource: 'services',
        randomize: true,
        dynamicLimit: 6,
        viewAll: {
          label: 'View All Specialities',
          href: ROUTE.specialities,
        },
      },
      {
        title: 'After Treatment',
        width: 'wide',
        items: [
          {
            label: 'Post-Surgery Recovery-LoveDay',
            href: ROUTE.postSurgery,
          },
        ],
        note: 'Post-surgery care is provided at partners with Loveday & Co',
      },
    ],
    featuredCard: undefined,
  },
  {
    id: 'pharmacy',
    label: 'Pharmacy',
    href: ROUTE.pharmacy,
    hasDropdown: true,
    columns: [
      {
        title: 'Shop Online',
        width: 'narrow',
        items: [
          {
            label: 'Browse All Products',
            href: `${EXTERNAL_LINKS.ONLINE_STORE}/collections/all`,
          },
          {
            label: 'Browse by Skincare',
            href: `${EXTERNAL_LINKS.ONLINE_STORE}/collections/skincare`,
          },
          {
            label: 'Browse by Vitamin & Supplements',
            href: `${EXTERNAL_LINKS.ONLINE_STORE}/collections/supplements`,
          },
        ],
      },
      {
        title: 'Pharmacy Services',
        width: 'narrow',
        items: [
          {
            label: 'Pharmacist Advices & Treatment',
            href: `${ROUTE.pharmacy}/#pharmacists-advices-and-treatment`,
          },
        ],
      },
      {
        title: 'In clinic Pharmacy',
        width: 'narrow',
        items: [
          {
            label: 'Our Pharmacy',
            href: ROUTE.pharmacy,
          },
          // { label: 'Opening hours & Contacts', href: '/pharmacy/contact/' },
        ],
      },
    ],
    featuredCard: {
      badge: 'CONVENIENT HEALTHCARE',
      title: 'Order from our online pharmacy',
      description:
        'Medicines, wellness products and skincare - delivered to your door. Existing patients can reorder prescriptions quickly without calling.',
      ctaLabel: 'Visit our online store',
      href: EXTERNAL_LINKS.ONLINE_STORE,
    },
  },
  {
    id: 'for-patients',
    label: 'For Patients',
    href: ROUTE.newPatientInformation,
    hasDropdown: true,
    columns: [
      {
        title: 'Before Your Visit',
        width: 'narrow',
        items: [
          {
            label: 'New Patient Information',
            href: ROUTE.newPatientInformation,
          },
          { label: 'Pricing & Payment', href: ROUTE.pricingAndPayments },
          // { label: 'Spread The Cost', href: '/spread-the-cost/' },
          // { label: 'Second Opinion', href: '/second-opinion/' },
          // { label: 'International Patients', href: '/international-patients/' },
          { label: 'Patient Referrals', href: ROUTE.forClinicians },
        ],
      },
      {
        title: 'Your Visit',
        width: 'narrow',
        items: [
          {
            label: 'Prepare for Your Appointment',
            href: ROUTE.preparingForYourAppointment,
            styles: 'whitespace-nowrap',
          },
          {
            label: 'How to Find Us: Getting Here, Parking and Accessibility',
            href: ROUTE.gettingHere,
          },
          // {
          //   label: 'Battersea Power Station: How to get there',
          //   href: '/how-to-get-there/',
          // },
        ],
      },
      {
        title: 'After Your Visit',
        width: 'narrow',
        items: [
          // { label: 'Post Treatment Support', href: '/post-treatment-support/' },
          {
            label: 'Your Feedback and Suggestions',
            href: ROUTE.leaveFeedback,
          },
          { label: 'Contact Us', href: ROUTE.contactUs },
        ],
      },
    ],
    featuredCard: {
      badge: 'USING HEALTH INSURANCE?',
      title: 'Check your cover before you book',
      description:
        'We work with 10+ leading insurers. Find out if your policy covers treatment at LIPS before making an appointment.',
      ctaLabel: 'Check my insurance',
      href: ROUTE.pricingAndPayments,
    },
  },
  {
    id: 'about-lips',
    label: 'About LIPS',
    href: ROUTE.aboutUs,
    hasDropdown: true,
    columns: [
      {
        title: 'Who we are',
        width: 'narrow',
        items: [
          { label: 'The Journey of LIPS', href: ROUTE.aboutUs },
          { label: 'Investor Relations', href: ROUTE.investorRelations },
          // { label: 'Nursing & care team', href: '/nursing-and-care-team/' },
          { label: 'For Clinicians & Referrals', href: ROUTE.careers },
        ],
      },
      {
        title: 'Our people',
        width: 'narrow',
        items: [
          { label: 'Meet Our Specialists', href: ROUTE.specialists },
          { label: 'Meet Our Nurses and Care Team', href: ROUTE.nurses },
          // { label: 'Patient reviews', href: '/leave-feedback/' },
        ],
      },
      {
        title: 'Stay Informed',
        width: 'narrow',
        items: [
          { label: 'Blogs', href: ROUTE.blogs },
          // { label: 'Events', href: ROUTE.technogymEvent },
          // { label: 'News & events', href: '/news-and-events/' },
        ],
      },
      {
        title: 'Find Us',
        width: 'narrow',
        items: [
          // {
          //   label: 'Battersea Power Station: How to get there',
          //   href: '/how-to-get-there/',
          // },
          {
            label: 'How to Find Us: Getting Here, Parking and Accessibility',
            href: ROUTE.gettingHere,
          },
          { label: 'Contact Us', href: ROUTE.contactUs },
        ],
      },
    ],
    featuredCard: undefined,
  },
];
